// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import './Button.css';
import SoundEffects from '../utils/system/SoundEffects';

/**
 * Reusable Button Component
 * Provides consistent styling and behavior for all buttons in the app
 * 
 * @param {string} variant - Button style: 'primary', 'secondary', 'danger', 'success'
 * @param {string} size - Button size: 'small', 'medium', 'large'
 * @param {string} icon - Optional icon (emoji or text)
 * @param {boolean} disabled - Disabled state
 * @param {boolean} loading - Loading state (shows spinner)
 * @param {function} onClick - Click handler
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {string} type - Button type: 'button', 'submit', 'reset'
 * @param {boolean} silent - If true, don't play click sound
 * @param {node} children - Button content
 */
const Button = ({
  variant = 'primary',
  size = 'medium',
  icon = null,
  disabled = false,
  loading = false,
  onClick,
  className = '',
  style = {},
  type = 'button',
  silent = false,
  children,
  ...props
}) => {
  const handleClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    
    // Play button click sound (unless silent prop is true)
    if (!silent) {
      SoundEffects.play('button_click', { volume: 0.3 });
    }
    
    if (onClick) {
      onClick(e);
    }
  };
  
  const handleMouseEnter = () => {
    if (!disabled && !loading) {
      SoundEffects.play('button_hover', { volume: 0.15 });
    }
  };

  const buttonClasses = [
    'btn',
    `btn-${variant}`,
    `btn-${size}`,
    disabled && 'btn-disabled',
    loading && 'btn-loading',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      disabled={disabled || loading}
      style={style}
      {...props}
    >
      {loading && (
        <span className="btn-spinner" aria-hidden="true">
          ⟳
        </span>
      )}
      {!loading && icon && (
        <span className="btn-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="btn-content">
        {children}
      </span>
    </button>
  );
};

export default Button;
